import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const GenreList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} genres`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  // Server-side sort (ref: survives refetches without re-creating getGenres)
  const sortRef = useRef({ sortBy: "", sortOrder: "" });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getGenres = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...sortRef.current,
          ...params,
        };

        console.log("Fetching genres with params:", requestParams);
        const response = await masterService.getGenres(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Genres API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const genresData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Genres data:", genresData);
          console.log("Pagination data:", paginationData);

          // Ensure genresData is an array
          if (Array.isArray(genresData)) {
            setGenres(genresData);
          } else {
            console.error("Genres data is not an array:", genresData);
            setGenres([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch genres" });
          setGenres([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching genres:", error);
        notify.error({ message: "Failed to fetch genres" });
        setGenres([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  // Load genres data. Mount only: every later fetch is an explicit getGenres(...)
  // from a search, status, or pagination handler. Re-running this whenever
  // getGenres is rebuilt would fire a second, redundant request alongside each of
  // them — and one per keystroke, which is what the debounce above exists to stop.
  useEffect(() => {
    setPageTitle("Genres");
    getGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getGenres({
        searchText: value.trim(),
        currentPage: 1,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getGenres({ searchText: (filters.searchText || "").trim(), currentPage: 1 });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getGenres({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    sortRef.current =
      sorter && sorter.order
        ? {
          sortBy: sorter.field,
          sortOrder: sorter.order === "ascend" ? "asc" : "desc",
        }
        : { sortBy: "", sortOrder: "" };

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getGenres({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add genre
  const handleAddGenre = () => {
    navigate("/genres/add/new");
  };

  // Handle edit genre
  const handleEditGenre = (genreId) => {
    navigate(`/genres/edit/${genreId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Genre",
      dataIndex: "genre_name",
      key: "genre_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.genre_name.localeCompare(b.genre_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Show in Preference",
      dataIndex: "show_in_preference",
      key: "show_in_preference",
      sorter: true,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Preference Order Number",
      dataIndex: "preference_order_number",
      key: "preference_order_number",

      render: (status) => (
        <Text strong style={{ fontSize: "14px" }}>
          {status}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "genre_is_active",
      key: "genre_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditGenre(record.genre_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Video Genres ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="All"
              style={{ width: 140 }}
            >
              <Option value="all">All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            <Search
              placeholder="Search genres..."
              allowClear
              value={filters.searchText}
              onChange={handleSearchChange}
              onSearch={handleSearchNow}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddGenre}
              size="middle"
            >
              Add Video Genre
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={genres}
          rowKey="genre_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No genres found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default GenreList;
